<template>
  <el-date-picker
    v-model="elValue"
    :type="type"
    :size="size"
    :align="align"
    :format="dformat"
    :disabled="disabled"
    :readonly="readonly"
    :editable="editable"
    :clearable="clearable"
    :placeholder="dPlaceholder"
    :default-time="dDefaultTime"
    :default-value="defaultValue"
    :picker-options="pickerOptions"
    :value-format="transFormat(valueFormat, true)"
    unlink-panels
    range-separator="——"
    :start-placeholder="dStartPlaceholder"
    :end-placeholder="dEndPlaceholder"
    @change="onChangeValue"
    :prefix-icon="icon"
  ></el-date-picker>
</template>


<script>
export default {
  name: 'customDatePicker',
  props: {
    size: [String],
    value: [Date, Array, String], // UTC世界时间(utc/str), 可以初始化赋值(但必须为valueFormat格式相同)
    initValue: [String, Array], // 初始化时间(时区时间)，格式为(YYYY-MM-DD HH:mm:ss)或(YYYY-MM-DD)
    format: [String], // 对显示的做格式化
    valueFormat: {
      type: String,
      default: 'yyyy-MM-dd HH:mm:ss',
    }, // 对返回的时间格式，只有utc格式(YYYY-MM-DD HH:mm:ss)才为utc时间，否则不做转换
    defaultValue: [Date],
    defaultTime: [String, Array],
    placeholder: {
      type: String,
    },
    startPlaceholder: {
      type: String,
    },
    endPlaceholder: {
      type: String,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    align: {
      type: String,
      default: 'left',
    },
    icon: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'date', // date/daterange等
    },
    pickerOptions: {
      type: Object,
      default() {
        return {
          firstDayOfWeek: 1,
        };
      },
    },
  },
  data() {
    return {
      pause: false, // 暂停value监听
      elValue: '', // ui组件绑定值（format参数格式）
      utcValue: '', // utc时间值（为YYYY-MM-DD HH:mm:ss格式）
      rangeTypes: ['years', 'months', 'dates', 'daterange', 'monthrange', 'datetimerange'], // 区间选择
    };
  },
  watch: {
    value() {
      if (!this.pause) {
        this.onValue();
      }
    },
    initValue() {
      this.onInitValue();
    },
  },
  computed: {
    // 日期显示格式化
    dformat() {
      if (this.format) {
        return this.$getElFormat(this.format, true);
      }
      if (['year', 'years'].includes(this.type)) {
        return 'yyyy';
      } else if (['month', 'months', 'monthrange'].includes(this.type)) {
        return this.$getElFormat('yyyy-MM');
      } else if (['date', 'dates', 'daterange'].includes(this.type)) {
        return this.$getElFormat('yyyy-MM-dd');
      }
      return this.$getElFormat();
    },
    // 默认时间
    dDefaultTime() {
      if (this.defaultTime) {
        return this.defaultTime;
      }
      return this.rangeTypes.includes(this.type) ? ['00:00:00', '23:59:59'] : '00:00:00';
    },
    dPlaceholder() {
      return this.placeholder || this.$t('Common.com_please_select_date');
    },
    dStartPlaceholder() {
      return this.startPlaceholder || this.$t('components_customDatePicker.kai_shi_ri_qi');
    },
    dEndPlaceholder() {
      return this.endPlaceholder || this.$t('components_customDatePicker.jie_shu_ri_qi');
    },
  },
  mounted() {
    this.onValue();
    this.onInitValue();
  },
  methods: {
    // 转换格式
    transFormat(formatStr, isEl) {
      if (isEl) {
        return formatStr.replace(/Y/g, 'y').replace(/D/g, 'd');
      }
      return formatStr.replace(/y/g, 'Y').replace(/d/g, 'D');
    },
    // 是否有值
    isHasValue(val) {
      let has = false;
      if (this.rangeTypes.includes(this.type)) {
        const isArr = Array.isArray(val);
        if (isArr && val.length > 0) {
          has = true;
        }
      } else if (val) {
        has = true;
      }
      return has;
    },
    // 赋值
    onValue() {
      const hasValue = this.isHasValue(this.value);
      if (!hasValue) {
        this.elValue = '';
        this.utcValue = '';
        return;
      }
      const regx = /\d{4}.\d{2}.\d{2}\s\d{2}:\d{2}:\d{2}/g;
      if (regx.test(this.value) || (this.value[0] && regx.test(this.value[0]))) {
        if (this.rangeTypes.includes(this.type)) {
          this.elValue = this.value.map((t) => this.$momentElLocal(t, this.transFormat(this.valueFormat, false)));
        } else {
          this.elValue = this.$momentElLocal(this.value, this.transFormat(this.valueFormat, false));
        }
        this.utcValue = this.value;
      } else {
        this.elValue = this.value;
        this.utcValue = '';
      }
    },
    // 初始值
    onInitValue() {
      const hasValue = this.isHasValue(this.initValue);
      if (!hasValue) {
        return;
      }
      if (this.rangeTypes.includes(this.type)) {
        this.elValue = this.initValue.map((t) => this.$moment(t).format(this.transFormat(this.valueFormat, false)));
        this.utcValue = this.initValue.map((t) => this.$momentWorld(t));
      } else {
        this.elValue = this.$moment(this.initValue).format(this.transFormat(this.valueFormat, false));
        this.utcValue = this.$momentWorld(this.initValue);
      }
      this.onUpdate(true);
    },
    // 日期选择触发事件
    onChangeValue(date) {
      const regx = /\D{4}.\D{2}.\D{2}\s\D{2}:\D{2}:\D{2}/g;
      // 只对包含时分秒时间格式对 utcValue 赋值
      if (regx.test(this.valueFormat)) {
        if (this.rangeTypes.includes(this.type)) {
          this.utcValue = date ? date.map((t) => this.$momentWorld(t)) : [];
        } else {
          this.utcValue = date ? this.$momentWorld(date) : '';
        }
      }
      this.onUpdate();
    },
    // 更新值
    onUpdate(init) {
      const regx = /\D{4}.\D{2}.\D{2}\s\D{2}:\D{2}:\D{2}/g;
      this.pause = true;
      // 只对包含时分秒时间格式才返回UTC格式
      if (regx.test(this.valueFormat)) {
        this.$emit('input', this.utcValue);
        this.$emit(init ? 'init' : 'change', this.utcValue);
      } else {
        this.$emit('input', this.elValue);
        this.$emit(init ? 'init' : 'change', this.elValue);
      }
      this.$nextTick(() => {
        this.pause = false;
      });
    },
  },
};
</script>

<style lang="scss"></style>
